import { isPlatformBrowser } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  Inject,
  Input,
  NgZone,
  OnDestroy,
  PLATFORM_ID,
  ViewChild,
} from '@angular/core';
import * as THREE from 'three';

interface LiquidUniforms {
  [uniform: string]: THREE.IUniform;
  uTime: THREE.IUniform<number>;
  uResolution: THREE.IUniform<THREE.Vector2>;
  uPointer: THREE.IUniform<THREE.Vector2>;
  uPointerVelocity: THREE.IUniform<THREE.Vector2>;
  uCursorSize: THREE.IUniform<number>;
  uColorA: THREE.IUniform<THREE.Color>;
  uColorB: THREE.IUniform<THREE.Color>;
  uColorC: THREE.IUniform<THREE.Color>;
}

@Component({
  selector: 'app-liquid-ether',
  template: '<div #container class="liquid-ether-container"></div>',
})
export class LiquidEtherComponent implements AfterViewInit, OnDestroy {
  @ViewChild('container', { static: true }) private containerRef!: ElementRef<HTMLDivElement>;

  @Input() colors: string[] = ['#053f32', '#0e6f4f', '#d8ffce'];
  @Input() mouseForce = 20;
  @Input() cursorSize = 100;
  @Input() isViscous = false;
  @Input() viscous = 30;
  @Input() iterationsViscous = 32;
  @Input() iterationsPoisson = 32;
  @Input() dt = 0.014;
  @Input() BFECC = true;
  @Input() resolution = 0.5;
  @Input() isBounce = false;
  @Input() autoDemo = true;
  @Input() autoSpeed = 0.5;
  @Input() autoIntensity = 2.2;
  @Input() takeoverDuration = 0.25;
  @Input() autoResumeDelay = 1000;
  @Input() autoRampDuration = 0.6;

  private readonly pointer = new THREE.Vector2(0, 0);
  private readonly pointerTarget = new THREE.Vector2(0, 0);
  private readonly pointerPrevious = new THREE.Vector2(0, 0);
  private readonly pointerVelocity = new THREE.Vector2(0, 0);
  private readonly autoPointer = new THREE.Vector2(0, 0);
  private readonly cleanups: Array<() => void> = [];

  private renderer: THREE.WebGLRenderer | null = null;
  private scene: THREE.Scene | null = null;
  private camera: THREE.Camera | null = null;
  private material: THREE.ShaderMaterial | null = null;
  private geometry: THREE.PlaneGeometry | null = null;
  private mesh: THREE.Mesh | null = null;
  private uniforms: LiquidUniforms | null = null;
  private resizeObserver: ResizeObserver | null = null;
  private intersectionObserver: IntersectionObserver | null = null;
  private frameId: number | null = null;
  private startTime = 0;
  private lastInteraction = 0;
  private running = false;
  private visible = true;

  constructor(
    @Inject(PLATFORM_ID) private readonly platformId: object,
    private readonly zone: NgZone,
  ) {}

  ngAfterViewInit(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this.zone.runOutsideAngular(() => {
      this.initialize();
    });
  }

  ngOnDestroy(): void {
    this.stopLoop();
    this.cleanups.splice(0).forEach((cleanup) => cleanup());
    this.resizeObserver?.disconnect();
    this.intersectionObserver?.disconnect();
    this.mesh = null;
    this.geometry?.dispose();
    this.material?.dispose();
    this.renderer?.dispose();
    this.renderer?.forceContextLoss();
    this.renderer?.domElement.remove();
    this.geometry = null;
    this.material = null;
    this.renderer = null;
    this.scene = null;
    this.camera = null;
    this.uniforms = null;
  }

  private initialize(): void {
    const container = this.containerRef.nativeElement;
    if (!this.hasWebGLSupport()) {
      container.classList.add('liquid-ether-fallback');
      return;
    }

    this.startTime = performance.now();
    this.lastInteraction = this.startTime;
    this.scene = new THREE.Scene();
    this.camera = new THREE.Camera();
    this.renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true, powerPreference: 'high-performance' });
    this.renderer.setClearColor(0x000000, 0);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.6));
    this.renderer.domElement.setAttribute('aria-hidden', 'true');
    this.renderer.domElement.classList.add('liquid-ether-canvas');
    container.prepend(this.renderer.domElement);

    const colorSet = this.normalizedColors();
    this.uniforms = {
      uTime: { value: 0 },
      uResolution: { value: new THREE.Vector2(1, 1) },
      uPointer: { value: this.pointer.clone() },
      uPointerVelocity: { value: this.pointerVelocity.clone() },
      uCursorSize: { value: 0.1 },
      uColorA: { value: new THREE.Color(colorSet[0]) },
      uColorB: { value: new THREE.Color(colorSet[1]) },
      uColorC: { value: new THREE.Color(colorSet[2]) },
    };

    this.geometry = new THREE.PlaneGeometry(2, 2);
    this.material = new THREE.ShaderMaterial({
      transparent: true,
      depthWrite: false,
      uniforms: this.uniforms,
      vertexShader: `
        varying vec2 vUv;

        void main() {
          vUv = uv;
          gl_Position = vec4(position.xy, 0.0, 1.0);
        }
      `,
      fragmentShader: `
        precision highp float;

        uniform float uTime;
        uniform vec2 uResolution;
        uniform vec2 uPointer;
        uniform vec2 uPointerVelocity;
        uniform float uCursorSize;
        uniform vec3 uColorA;
        uniform vec3 uColorB;
        uniform vec3 uColorC;
        varying vec2 vUv;

        float hash(vec2 p) {
          return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123);
        }

        float noise(vec2 p) {
          vec2 i = floor(p);
          vec2 f = fract(p);
          vec2 u = f * f * (3.0 - 2.0 * f);
          return mix(
            mix(hash(i + vec2(0.0, 0.0)), hash(i + vec2(1.0, 0.0)), u.x),
            mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), u.x),
            u.y
          );
        }

        float fbm(vec2 p) {
          float value = 0.0;
          float amplitude = 0.5;
          for (int i = 0; i < 5; i++) {
            value += amplitude * noise(p);
            p = p * 2.03 + vec2(17.12, 8.37);
            amplitude *= 0.52;
          }
          return value;
        }

        void main() {
          vec2 aspect = vec2(max(uResolution.x / max(uResolution.y, 1.0), 1.0), 1.0);
          vec2 p = (vUv * 2.0 - 1.0) * aspect;
          vec2 m = uPointer * aspect;
          vec2 velocity = uPointerVelocity * aspect;
          float energy = clamp(length(velocity) * 12.0, 0.0, 1.0);
          float dist = distance(p, m);
          float wake = smoothstep(uCursorSize * 2.6, 0.0, dist);
          vec2 pull = normalize(p - m + vec2(0.002, -0.001));
          vec2 flow = p;
          flow += vec2(
            sin((p.y + uTime * 0.12) * 2.7),
            cos((p.x - uTime * 0.1) * 2.15)
          ) * 0.16;
          flow += pull * wake * (0.34 + energy * 0.55);
          flow += velocity * wake * 1.8;

          float liquid = fbm(flow * 2.25 + vec2(uTime * 0.035, -uTime * 0.025));
          float ribbon = sin((flow.x - flow.y) * 3.75 + liquid * 4.2 + uTime * 0.38) * 0.5 + 0.5;
          vec3 color = mix(uColorA, uColorB, smoothstep(0.16, 0.92, liquid));
          color = mix(color, uColorC, smoothstep(0.42, 0.96, ribbon) * 0.5);
          color = mix(color, uColorC, wake * (0.32 + energy * 0.28));

          float vignette = smoothstep(1.72, 0.16, length(p * vec2(0.75, 1.0)));
          float alpha = clamp(0.34 + liquid * 0.34 + wake * 0.2, 0.28, 0.86) * vignette;
          gl_FragColor = vec4(color, alpha);
        }
      `,
    });
    this.mesh = new THREE.Mesh(this.geometry, this.material);
    this.scene.add(this.mesh);

    this.bindEvents(container);
    this.resize();
    this.startLoop();
  }

  private bindEvents(container: HTMLDivElement): void {
    const view = container.ownerDocument.defaultView;
    if (!view) {
      return;
    }

    const onPointerMove = (event: MouseEvent): void => {
      this.updatePointer(event.clientX, event.clientY);
    };
    const onTouchMove = (event: TouchEvent): void => {
      const touch = event.touches.item(0);
      if (!touch) {
        return;
      }

      this.updatePointer(touch.clientX, touch.clientY);
    };
    const onVisibilityChange = (): void => {
      this.visible = !container.ownerDocument.hidden;
      if (this.visible) {
        this.startLoop();
      } else {
        this.stopLoop();
      }
    };

    view.addEventListener('mousemove', onPointerMove);
    view.addEventListener('touchmove', onTouchMove, { passive: true });
    container.ownerDocument.addEventListener('visibilitychange', onVisibilityChange);
    this.cleanups.push(() => view.removeEventListener('mousemove', onPointerMove));
    this.cleanups.push(() => view.removeEventListener('touchmove', onTouchMove));
    this.cleanups.push(() => container.ownerDocument.removeEventListener('visibilitychange', onVisibilityChange));

    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.resize());
      this.resizeObserver.observe(container);
    } else {
      const onResize = (): void => this.resize();
      view.addEventListener('resize', onResize);
      this.cleanups.push(() => view.removeEventListener('resize', onResize));
    }

    if (typeof IntersectionObserver !== 'undefined') {
      this.intersectionObserver = new IntersectionObserver((entries) => {
        const entry = entries[0];
        this.visible = Boolean(entry?.isIntersecting);
        if (this.visible) {
          this.startLoop();
        } else {
          this.stopLoop();
        }
      });
      this.intersectionObserver.observe(container);
    }
  }

  private startLoop(): void {
    if (this.running || !this.renderer || !this.scene || !this.camera) {
      return;
    }

    this.running = true;
    this.frameId = window.requestAnimationFrame(this.renderFrame);
  }

  private stopLoop(): void {
    this.running = false;
    if (this.frameId !== null) {
      window.cancelAnimationFrame(this.frameId);
      this.frameId = null;
    }
  }

  private readonly renderFrame = (): void => {
    if (!this.running || !this.renderer || !this.scene || !this.camera || !this.uniforms) {
      return;
    }

    const now = performance.now();
    const elapsed = (now - this.startTime) / 1000;
    if (this.autoDemo && now - this.lastInteraction > this.autoResumeDelay) {
      this.driveAutoPointer(now);
    }

    const viscosityEase = this.isViscous ? 0.05 : 0.12;
    const blend = Math.min(0.28, Math.max(0.04, viscosityEase + this.dt * 1.4));
    this.pointer.lerp(this.pointerTarget, blend);
    this.pointerVelocity.subVectors(this.pointer, this.pointerPrevious);
    this.pointerVelocity.multiplyScalar((this.mouseForce / 20) * (this.autoDemo ? this.autoIntensity : 1));
    this.pointerPrevious.copy(this.pointer);

    const colorSet = this.normalizedColors();
    this.uniforms.uTime.value = elapsed;
    this.uniforms.uPointer.value.copy(this.pointer);
    this.uniforms.uPointerVelocity.value.copy(this.pointerVelocity);
    this.uniforms.uCursorSize.value = this.normalizedCursorSize();
    this.uniforms.uColorA.value.set(colorSet[0]);
    this.uniforms.uColorB.value.set(colorSet[1]);
    this.uniforms.uColorC.value.set(colorSet[2]);
    this.renderer.render(this.scene, this.camera);

    if (this.visible) {
      this.frameId = window.requestAnimationFrame(this.renderFrame);
    }
  };

  private updatePointer(clientX: number, clientY: number): void {
    const rect = this.containerRef.nativeElement.getBoundingClientRect();
    if (
      rect.width <= 0 ||
      rect.height <= 0 ||
      clientX < rect.left ||
      clientX > rect.right ||
      clientY < rect.top ||
      clientY > rect.bottom
    ) {
      return;
    }

    const x = ((clientX - rect.left) / rect.width) * 2 - 1;
    const y = -(((clientY - rect.top) / rect.height) * 2 - 1);
    this.pointerTarget.set(x, y);
    this.lastInteraction = performance.now();
  }

  private driveAutoPointer(now: number): void {
    const idleDuration = Math.max(1, this.autoRampDuration * 1000);
    const ramp = Math.min(1, Math.max(0, (now - this.lastInteraction - this.autoResumeDelay) / idleDuration));
    const t = ((now - this.startTime) / 1000) * this.autoSpeed;
    this.autoPointer.set(Math.sin(t * 0.91) * 0.72 * ramp, Math.cos(t * 0.67) * 0.56 * ramp);
    this.pointerTarget.lerp(this.autoPointer, 0.08);
  }

  private resize(): void {
    if (!this.renderer || !this.uniforms) {
      return;
    }

    const rect = this.containerRef.nativeElement.getBoundingClientRect();
    const width = Math.max(1, Math.floor(rect.width));
    const height = Math.max(1, Math.floor(rect.height));
    this.renderer.setSize(width, height, false);
    this.uniforms.uResolution.value.set(width * this.resolution, height * this.resolution);
    this.uniforms.uCursorSize.value = this.normalizedCursorSize();
  }

  private normalizedCursorSize(): number {
    const rect = this.containerRef.nativeElement.getBoundingClientRect();
    const base = Math.max(1, Math.min(rect.width || 1, rect.height || 1));
    return Math.max(0.04, Math.min(0.32, this.cursorSize / base));
  }

  private normalizedColors(): [string, string, string] {
    const fallback: [string, string, string] = ['#053f32', '#0e6f4f', '#d8ffce'];
    const valid = this.colors.filter((color) => /^#[0-9a-f]{3,8}$/i.test(color));
    return [
      valid[0] ?? fallback[0],
      valid[1] ?? valid[0] ?? fallback[1],
      valid[2] ?? valid[1] ?? valid[0] ?? fallback[2],
    ];
  }

  private hasWebGLSupport(): boolean {
    try {
      const canvas = document.createElement('canvas');
      const getContext = canvas.getContext.bind(canvas) as (contextId: string) => RenderingContext | null;
      return Boolean(getContext('webgl') || getContext('experimental-webgl'));
    } catch {
      return false;
    }
  }
}
